import React, { useEffect } from "react";
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaHeartbeat,
  FaShieldAlt,
  FaUserMd,
  FaAward,
  FaFlask,
  FaCheckCircle,
  FaUsers,
  FaHospital,
  FaClock
} from "react-icons/fa";
import { Link } from "react-router-dom";

export default function AboutUs() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const stats = [
    { value: "500+", label: "Certified Partner Labs", icon: <FaHospital /> },
    { value: "100K+", label: "Happy Patients Served", icon: <FaUsers /> },
    { value: "99.8%", label: "Accuracy in Diagnostics", icon: <FaAward /> },
    { value: "6 Hours", label: "Fast Digital Reports", icon: <FaClock /> },
  ];

  const coreValues = [
    {
      icon: <FaShieldAlt size={22} />,
      title: "100% NABL & ISO Accredited",
      desc: "Every lab in our network conforms to highest diagnostic protocols ensuring gold-standard accuracy in clinical findings.",
      badge: "Quality Guarantee"
    },
    {
      icon: <FaHeartbeat size={22} />,
      title: "Patient-First Healthcare",
      desc: "Painless doorstep sample collection by verified phlebotomists with zero home collection charges on eligible bookings.",
      badge: "Patient Care"
    },
    {
      icon: <FaFlask size={22} />,
      title: "Advanced Technology",
      desc: "Automated sample barcode tracking, temperature-controlled transit boxes, and digital report validation.",
      badge: "Smart Labs"
    },
    {
      icon: <FaUserMd size={22} />,
      title: "Pathologist Consultation",
      desc: "Free tele-consultation support to help interpret and understand vital test biomarkers and health parameters.",
      badge: "Doctor Support"
    },
  ];

  return (
    <div className="w-full min-h-screen bg-slate-50 text-slate-800">
      
      {/* HERO SECTION */}
      <section className="relative bg-gradient-to-br from-red-600 via-red-700 to-rose-800 text-white py-16 md:py-24 px-4 sm:px-6 lg:px-12 overflow-hidden">
        {/* Background glow ornaments */}
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/15 text-white border border-white/20 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5 backdrop-blur-xs">
            <FaAward className="text-amber-300" />
            <span>India's Trusted Digital Diagnostics Platform</span>
          </div>

          <h1 className="text-3xl sm:text-5xl md:text-6xl font-black tracking-tight text-white leading-tight">
            Empowering Health Through <br className="hidden sm:inline" />
            <span className="text-amber-200">
              Precision Diagnostics
            </span>
          </h1>

          <p className="mt-5 text-sm sm:text-base md:text-lg text-red-100 max-w-3xl mx-auto font-normal leading-relaxed">
            MedrphaLab connects millions of citizens with certified pathology laboratories, expert phlebotomists, and fast clinical reports across India.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/all-tests"
              className="px-6 py-3 bg-white text-red-600 hover:bg-red-50 rounded-xl text-sm font-bold shadow-lg shadow-black/10 transition-all hover:scale-105"
            >
              Explore Tests & Packages
            </Link>
            <Link
              to="/partner-with-us"
              className="px-6 py-3 bg-red-800/80 hover:bg-red-800 text-white border border-red-400/40 rounded-xl text-sm font-bold transition-all"
            >
              Partner With Us
            </Link>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="max-w-6xl mx-auto px-4 -mt-8 sm:-mt-12 relative z-20">
        <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl border border-slate-100 p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, idx) => (
            <div key={idx} className="text-center">
              <div className="w-10 h-10 mx-auto rounded-xl bg-red-50 text-red-600 flex items-center justify-center text-lg mb-2 shadow-xs">
                {stat.icon}
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">
                {stat.value}
              </h3>
              <p className="text-xs font-semibold text-slate-500 mt-1">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CORE VALUES / FEATURES */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-bold text-red-600 uppercase tracking-wider bg-red-50 px-3.5 py-1 rounded-full border border-red-100">
            Why MedrphaLab
          </span>
          <h2 className="text-2xl md:text-3xl font-black text-slate-900 mt-3 tracking-tight">
            Setting New Benchmarks In Clinical Diagnostics
          </h2>
          <p className="text-xs md:text-sm text-slate-600 mt-2">
            Combining medical excellence with seamless digital convenience.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {coreValues.map((val, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl p-6 sm:p-7 border border-slate-200/80 shadow-xs hover:shadow-lg hover:border-red-200 transition-all duration-300 group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center group-hover:bg-red-600 group-hover:text-white transition-all">
                  {val.icon}
                </div>
                <span className="text-[10px] font-bold text-red-700 bg-red-50 px-2.5 py-1 rounded-full border border-red-100">
                  {val.badge}
                </span>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                {val.title}
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                {val.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CORPORATE / REGULATORY TRANSPARENCY */}
      <section className="bg-white border-y border-slate-200/80 py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-xs font-bold text-red-600 uppercase tracking-wider bg-red-50 px-3 py-1 rounded-full border border-red-100">
              Corporate Governance
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mt-3 tracking-tight leading-snug">
              ARMC PHARMA PRIVATE LIMITED
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-3 leading-relaxed">
              MedrphaLab is operated by ARMC PHARMA PRIVATE LIMITED. We are a registered healthcare technology enterprise bridging the gap between world-class certified pathology laboratories and patients across urban and rural India.
            </p>

            <div className="mt-6 space-y-3">
              <div className="flex items-start gap-3">
                <FaCheckCircle className="text-emerald-500 mt-1 shrink-0" />
                <p className="text-xs text-slate-700 font-medium">
                  Compliant with Clinical Establishments Act & DPDP Act 2023.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <FaCheckCircle className="text-emerald-500 mt-1 shrink-0" />
                <p className="text-xs text-slate-700 font-medium">
                  End-to-end encrypted health records and report privacy protocols.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <FaCheckCircle className="text-emerald-500 mt-1 shrink-0" />
                <p className="text-xs text-slate-700 font-medium">
                  Real-time temperature and phlebotomy barcoding tracking.
                </p>
              </div>
            </div>
          </div>

          {/* CONTACT DETAIL CARD */}
          <div className="bg-gradient-to-br from-red-600 via-red-700 to-rose-800 text-white p-7 rounded-3xl shadow-xl border border-red-500">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-amber-300" />
              Corporate Headquarters
            </h3>

            <div className="space-y-4 text-xs sm:text-sm text-red-50">
              <div className="flex items-start gap-3.5 bg-white/10 backdrop-blur-xs p-3.5 rounded-xl border border-white/15">
                <FaMapMarkerAlt className="text-amber-300 text-base mt-1 shrink-0" />
                <div>
                  <strong className="text-white block">Registered Address:</strong>
                  ARMC PHARMA PRIVATE LIMITED<br />
                  D-43 1st Floor, Sector 6, Noida, Uttar Pradesh - 201301
                </div>
              </div>

              <div className="flex items-center gap-3.5 bg-white/10 backdrop-blur-xs p-3.5 rounded-xl border border-white/15">
                <FaPhone className="text-amber-300 text-base shrink-0" />
                <div>
                  <strong className="text-white block">Helpline / Support:</strong>
                  <a href="tel:8178399885" className="hover:text-white underline">+91 81783 99885</a>
                </div>
              </div>

              <div className="flex items-center gap-3.5 bg-white/10 backdrop-blur-xs p-3.5 rounded-xl border border-white/15">
                <FaEnvelope className="text-amber-300 text-base shrink-0" />
                <div>
                  <strong className="text-white block">Official Email:</strong>
                  <a href="mailto:info@medrpha.com" className="hover:text-white underline">info@medrpha.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
